import qs from 'qs'
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query'
import { isDefined } from '@chakra-ui/utils'
import fetch from '~/common/fetch'
import { queryClient } from '~/common/react-query/queryClient'
import { Log } from '~/settings/settingsApi'
import { VendorPreview } from '~/api/vendors'
import { OffsetLimit, OffsetLimitPaginationResponse } from './common'

export interface ProgramTeam {
  id: number
  name: string
}

export type ProgramTeamMatchedCriteria = {
  label: string
  display_value: string
}

export type ProgramTeamByRules = ProgramTeam & {
  matched_criteria?: ProgramTeamMatchedCriteria[]
}

export type ProgramTeamOverview = {
  id: number
  name: string
  display_id: string
  status: number
  created_at: string
  updated_at: string
  active_engagements?: number
}

export type ProgramTeamDetailsType = {
  profile: ProgramTeamOverview
}

export type ProgramTeamSummary = {
  id: number
  name: string
  display_id: string
  created_at: string
  status: number
  active_engagements: number
}

export const getProgramTeamsConfig = (params: Record<string, any>) => {
  return {
    queryKey: [PROGRAM_TEAM_QUERY_KEY, 'list', params],
    queryFn: (): OffsetLimitPaginationResponse<ProgramTeamSummary> =>
      fetch.get(`/api/v2/program-team/?${qs.stringify(params, { arrayFormat: 'comma' })}`),
    getNextPageParam: (lastPage: OffsetLimitPaginationResponse<ProgramTeamSummary>) =>
      lastPage.next_params?.offset ?? false
  }
}

export const useProgramTeamDetails = (id: number | undefined) =>
  useQuery({
    queryKey: [PROGRAM_TEAM_QUERY_KEY, id, 'details'],
    queryFn: (): ProgramTeamDetailsType => fetch.get(`/api/v2/program-team/${id}/`),
    staleTime: 1000 * 60 * 5,
    enabled: id != null
  })

export const useProgramTeamConfig = () =>
  useQuery({
    queryKey: [PROGRAM_TEAM_QUERY_KEY, 'config'],
    queryFn: (): ProgramTeamConfigType => fetch.get(`/api/v2/program-team/config/`),
    staleTime: 1000 * 60 * 5
  })

export type ProgramTeamConfigType = {
  can_view?: boolean
  can_create?: boolean
  can_update?: boolean
  can_add_member?: boolean
  can_remove_user?: boolean
}

export const PROGRAM_TEAM_QUERY_KEY = 'program-team'
const VENDOR_QUERY_KEY = 'vendor'

export type ProgramTeamFormData = {
  name?: string | undefined
}

export type ProgramTeamRoleBindingFormData = {
  program_entity: number
  user: number
  roles: number[]
}

export const programTeamApi = {
  removeVendor: (programEntityId: number, vendorEntityId: number): Promise<void> =>
    fetch.post(`/api/v2/program-team/${programEntityId}/remove_vendor/`, { vendor_entity_id: vendorEntityId })
}

export const useCreateProgramTeam = () =>
  useMutation({
    mutationFn: (values: ProgramTeamFormData) => {
      return fetch.post('/api/v2/program-team', values)
    },
    onSuccess: () => {
      queryClient.invalidateQueries([PROGRAM_TEAM_QUERY_KEY])
    }
  })

export const useUpdateProgramTeam = (id: number) =>
  useMutation({
    mutationFn: (values: ProgramTeamFormData) => {
      return fetch.put(`/api/v2/program-team/${id}`, values)
    },
    onSuccess: () => {
      queryClient.invalidateQueries([PROGRAM_TEAM_QUERY_KEY, id])
    }
  })

const getActivityLog = (
  programTeamId: number,
  params: Partial<OffsetLimit>
): Promise<OffsetLimitPaginationResponse<Log>> =>
  fetch.get(`/api/v2/program-team/${programTeamId}/activity_log/${qs.stringify(params, { addQueryPrefix: true })}`)

export const useProgramTeamActivityLog = (id: number, filters?: Partial<OffsetLimit>) => {
  return useInfiniteQuery({
    queryKey: [PROGRAM_TEAM_QUERY_KEY, id, 'activity-log'],
    queryFn: ({ pageParam = 0 }) => {
      return getActivityLog(id, { offset: pageParam, ...filters })
    },
    getNextPageParam: lastPage => lastPage.next_params?.offset ?? false
  })
}

export type MultiPmoEnabledResponse = {
  enabled: boolean
}

export const useMultiPmoEnabled = () =>
  useQuery({
    queryKey: [PROGRAM_TEAM_QUERY_KEY, 'multi-pmo-enabled'],
    queryFn: (): MultiPmoEnabledResponse => fetch.get('/api/v2/program-team/multi_pmo_enabled/'),
    staleTime: 1000 * 60 * 5 // Cache for 5 minutes since this shouldn't change often
  })

export interface ProgramTeamMember {
  id: number
  profile_id: number
  username: string
  full_name: string | null
  email: string
  program_entity_roles: string[]
}

export const useProgramTeamMembers = (programTeamId: number | undefined, params: Record<string, any>) => {
  return useQuery({
    queryKey: [PROGRAM_TEAM_QUERY_KEY, programTeamId, 'members', params],
    queryFn: (): OffsetLimitPaginationResponse<ProgramTeamMember> =>
      fetch.get(`/api/v2/program-team-users/?${qs.stringify({ ...params, program_team: programTeamId })}`),
    enabled: programTeamId != null,
    staleTime: 1000 * 60 * 2
  })
}

export const useProgramTeamRoleBindings = (userId: number | undefined) => {
  return useQuery({
    queryKey: [PROGRAM_TEAM_QUERY_KEY, userId, 'role-bindings'],
    queryFn: (): any[] => fetch.get(`/api/v2/program-team-role-bindings/?user=${userId}`),
    enabled: isDefined(userId),
    staleTime: 1000 * 60 * 2
  })
}

export const useCreateProgramTeamRoleBinding = (programTeamId: number) => {
  return useMutation({
    mutationFn: (values: ProgramTeamRoleBindingFormData) => fetch.post(`/api/v2/program-team-role-bindings/`, values),
    onSuccess: () => {
      queryClient.invalidateQueries([PROGRAM_TEAM_QUERY_KEY, programTeamId])
    }
  })
}

export type RemoveUserWarningReasons = 'active_jobs' | 'work_orders' | 'applicants'

export const WARNING_REASON_MSG_IDS: Record<string, string> = {
  active_jobs: 'program_team.remove_member.warning_modal.reason.active_jobs',
  work_orders: 'program_team.remove_member.warning_modal.reason.work_orders',
  applicants: 'program_team.remove_member.warning_modal.reason.applicants'
}

export function getRemoveUserFromProgramTeamWarnings(
  programEntityId: number,
  userId: number
): Promise<{ warning_reasons: RemoveUserWarningReasons[] }> {
  return fetch.get(
    `/api/v2/program-team-role-bindings/remove_user_warnings/?program_entity=${programEntityId}&user=${userId}`
  )
}

export const useRemoveUserFromProgramTeam = (programTeamId: number) => {
  return useMutation({
    mutationFn: (userId: number) =>
      fetch.delete(`/api/v2/program-team-role-bindings/remove_user/?program_entity=${programTeamId}&user=${userId}`),
    onSuccess: () => {
      queryClient.invalidateQueries([PROGRAM_TEAM_QUERY_KEY, programTeamId])
    }
  })
}

// todo: (shubdas) create a separate api for program team vendors
export const getVendorsConfig = (params: Record<string, any>) => {
  return {
    queryKey: [VENDOR_QUERY_KEY, 'list', params],
    queryFn: (): OffsetLimitPaginationResponse<VendorPreview> =>
      fetch.get(`/api/v1/vendors/summaries?${qs.stringify(params, { arrayFormat: 'comma' })}`),
    getNextPageParam: (lastPage: OffsetLimitPaginationResponse<VendorPreview>) => lastPage.next_params?.offset ?? false
  }
}

export const useRemoveVendorFromProgramTeam = () => {
  return useMutation({
    mutationFn: ({ programEntityId, vendorEntityId }: { programEntityId: number; vendorEntityId: number }) =>
      programTeamApi.removeVendor(programEntityId, vendorEntityId),
    onSuccess: () => {
      queryClient.invalidateQueries([VENDOR_QUERY_KEY, 'list'])
      queryClient.invalidateQueries([PROGRAM_TEAM_QUERY_KEY])
    }
  })
}

export interface GetProgramEntityByRulesParams {
  country_id?: number
  region_id?: number
  subdivision_id?: number
  work_site_id?: number
  job_template_job_type_id?: number
  job_template_id?: number
  job_category_id?: number
  module?: number
  business_unit_id?: number
}

export const useGetProgramEntityByRules = (params: GetProgramEntityByRulesParams, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: [PROGRAM_TEAM_QUERY_KEY, 'get-by-rules', params],
    queryFn: ({ signal }): Promise<ProgramTeamByRules | null> => {
      const queryString = qs.stringify(params, { skipNulls: true, arrayFormat: 'comma' })
      return fetch.get(`/api/v2/program-team/get_by_rules/?${queryString}`, { signal })
    },
    enabled:
      (options?.enabled !== false && Object.keys(params).length > 0 && Object.values(params).some(v => v != null)) ??
      false,
    staleTime: 0 // Always refetch when parameters change
  })
}
